import _ from 'lodash';
import {generateGuid} from '@packages/utilities/guid';
import {
  ERROR_STATE,
  UPDATE_ACCOUNT_ERROR_CODES,
  RESERVED_USER_FIELDS,
  updateAccountErrorStates,
} from '@packages/systems/users/constants';
import type {Context} from '../types';
import type {JSONNode} from '@packages/shared/types/JSONNode';

const hasUserAccountFormWrapper = (
  node: JSONNode,
  nodeMap: Map<string, JSONNode>
): boolean => {
  if (!node || !node.children) return false;

  if (node.type === 'UserAccountFormWrapper') return true;

  return _.some(node.children, (childId) => {
    const childNode = nodeMap.get(childId);
    return childNode && hasUserAccountFormWrapper(childNode, nodeMap);
  });
};

export default (context: Context) => {
  const {
    node: {jsonNode},
    tree: {nodeMap},
    fn: {insertNodesAfter},
  } = context;
  const {type, children = []} = jsonNode;
  const needed =
    type === 'UserAccountWrapper' &&
    !hasUserAccountFormWrapper(jsonNode, nodeMap);

  return {
    needed,
    logData: {
      msg: 'A User Account Form should be added to the User Account page',
      meta: {
        nodeId: jsonNode._id,
      },
    },
    apply: () => {
      const userAccountFormWrapperId = generateGuid();
      const userAccountFormId = generateGuid();
      const userFormSuccessStateId = generateGuid();
      const userFormErrorStateId = generateGuid();
      const userEmailLabelId = generateGuid();
      const userEmailLabelTextId = generateGuid();
      const userEmailInputId = generateGuid();
      const userNameLabelId = generateGuid();
      const userNameLabelTextId = generateGuid();
      const userNameInputId = generateGuid();
      const passwordHeadingId = generateGuid();
      const passwordHeadingTextId = generateGuid();
      const resetPasswordLinkId = generateGuid();
      const resetPasswordLinkTextId = generateGuid();
      const communicationsCheckboxWrapperId = generateGuid();
      const userAccountFormSaveButtonId = generateGuid();
      const userAccountFormCancelButtonId = generateGuid();
      const communicationsCheckboxId = generateGuid();
      const communicationsCheckboxLabelId = generateGuid();
      const communicationsCheckboxLabelTextId = generateGuid();
      const userSuccessMsgId = generateGuid();
      const userSuccessMsgTextId = generateGuid();
      const userErrorMsgId = generateGuid();

      const userAccountFormWrapperNode = [
        {
          _id: userAccountFormWrapperId,
          type: 'UserAccountFormWrapper',
          tag: 'div',
          children: [
            userAccountFormId,
            userFormSuccessStateId,
            userFormErrorStateId,
          ],
        },
        {
          _id: userAccountFormId,
          type: 'UserAccountForm',
          tag: 'div',
          data: {type: 'user-account'},
          children: [
            userEmailLabelId,
            userEmailInputId,
            userNameLabelId,
            userNameInputId,
            passwordHeadingId,
            resetPasswordLinkId,
            communicationsCheckboxWrapperId,
            userAccountFormSaveButtonId,
            userAccountFormCancelButtonId,
          ],
        },
        {
          _id: userEmailLabelId,
          type: 'FormBlockLabel',
          tag: 'label',
          data: {
            attr: {
              for: 'Email',
            },
          },
          children: [userEmailLabelTextId],
        },
        {_id: userEmailLabelTextId, text: true, v: 'Email'},
        {
          _id: userEmailInputId,
          type: 'UserFormEmailInput',
          tag: 'div',
          data: {
            attr: {
              autofocus: false,
              disabled: true,
              id: 'wf-user-account-email',
              name: 'Email',
              placeholder: '',
            },
          },
        },
        {
          _id: userNameLabelId,
          type: 'FormBlockLabel',
          tag: 'label',
          data: {
            attr: {
              for: 'Name',
            },
          },
          children: [userNameLabelTextId],
        },
        {_id: userNameLabelTextId, text: true, v: 'Name'},
        {
          _id: userNameInputId,
          type: 'UserFormTextInput',
          tag: 'div',
          data: {
            attr: {
              autofocus: false,
              'data-name': 'field',
              disabled: false,
              id: 'wf-user-account-name',
              maxlength: 256,
              name: '',
              placeholder: '',
              required: true,
              type: 'text',
            },
            form: {name: 'name', type: 'input', passwordPage: false},
            users: {field: RESERVED_USER_FIELDS.name},
          },
        },
        {
          _id: passwordHeadingId,
          type: 'Heading',
          tag: 'h3',
          data: {tag: 'h3'},
          children: [passwordHeadingTextId],
        },
        {
          _id: passwordHeadingTextId,
          text: true,
          v: 'Password settings',
        },
        {
          _id: resetPasswordLinkId,
          type: 'Link',
          tag: 'a',
          data: {
            block: '',
            button: false,
            link: {mode: 'external', url: '/reset-password'},
          },
          children: [resetPasswordLinkTextId],
        },
        {
          _id: resetPasswordLinkTextId,
          text: true,
          v: 'Reset password',
        },
        {
          _id: communicationsCheckboxWrapperId,
          type: 'FormCheckboxWrapper',
          tag: 'div',
          data: {
            form: {type: 'checkbox'},
            style: {
              base: {
                main: {noPseudo: {marginTop: '20px', marginBottom: '20px'}},
              },
            },
          },
          children: [communicationsCheckboxId, communicationsCheckboxLabelId],
        },
        {
          _id: communicationsCheckboxId,
          type: 'UserFormCheckboxInput',
          tag: 'div',
          data: {
            users: {field: RESERVED_USER_FIELDS.acceptCommunications},
            form: {type: 'checkbox-input', name: ''},
            inputType: 'default',
            attr: {
              checked: true,
              'data-name': 'Checkbox',
              id: 'wf-user-account-accept-communications',
              name: 'Checkbox',
              required: true,
              type: 'checkbox',
            },
          },
        },
        {
          _id: communicationsCheckboxLabelId,
          type: 'FormInlineLabel',
          tag: 'label',
          data: {
            form: {type: 'checkbox-label', name: ''},
          },
          children: [communicationsCheckboxLabelTextId],
        },
        {
          _id: communicationsCheckboxLabelTextId,
          text: true,
          v: 'I consent to receive marketing emails.',
        },
        {
          _id: userAccountFormSaveButtonId,
          type: 'UserAccountFormSaveButton',
          tag: 'div',
          data: {
            attr: {
              'data-wait': 'Saving...',
              disabled: false,
              type: 'submit',
              value: 'Save Changes',
            },
          },
        },
        {
          _id: userAccountFormCancelButtonId,
          type: 'UserAccountFormCancelButton',
          tag: 'div',
          data: {
            attr: {value: 'Cancel', type: 'reset', disabled: false},
            style: {
              base: {
                main: {
                  noPseudo: {
                    borderBottomColor: '#e6e6e6',
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderLeftColor: '#e6e6e6',
                    borderLeftStyle: 'solid',
                    borderLeftWidth: '1px',
                    borderRightColor: '#e6e6e6',
                    borderRightStyle: 'solid',
                    borderRightWidth: '1px',
                    borderTopColor: '#e6e6e6',
                    borderTopStyle: 'solid',
                    borderTopWidth: '1px',
                  },
                },
              },
            },
          },
        },
        {
          _id: userFormSuccessStateId,
          type: 'UserFormSuccessState',
          tag: 'div',
          data: {
            attr: {tabIndex: '-1'},
            style: {
              base: {
                main: {
                  noPseudo: {marginTop: '10px'},
                },
              },
            },
          },
          children: [userSuccessMsgId],
        },
        {
          _id: userSuccessMsgId,
          type: 'Paragraph',
          tag: 'p',
          children: [userSuccessMsgTextId],
        },
        {
          _id: userSuccessMsgTextId,
          text: true,
          v: 'Your account was updated successfully.',
        },
        {
          _id: userFormErrorStateId,
          type: 'UserFormErrorState',
          tag: 'div',
          data: {
            style: {
              base: {
                main: {
                  noPseudo: {
                    marginLeft: '0px',
                    marginRight: '0px',
                    position: 'initial',
                  },
                },
              },
            },
            users: {type: ERROR_STATE.ACCOUNT_UPDATE},
          },
          children: [userErrorMsgId],
        },
        {
          _id: userErrorMsgId,
          type: 'UserErrorMsg',
          tag: 'div',
          data: {
            users: {type: ERROR_STATE.ACCOUNT_UPDATE},
            errors: [
              {
                copy: updateAccountErrorStates[
                  UPDATE_ACCOUNT_ERROR_CODES.GENERAL_ERROR
                ]?.copy,
                id: UPDATE_ACCOUNT_ERROR_CODES.GENERAL_ERROR,
                name: updateAccountErrorStates[
                  UPDATE_ACCOUNT_ERROR_CODES.GENERAL_ERROR
                ]?.name,
              },
            ],
          },
        },
      ];

      // insert after the last child within the UserAccountWrapper
      const lastChildElementId = children[children.length - 1];
      children.push(userAccountFormWrapperId);
      // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
      insertNodesAfter(lastChildElementId, userAccountFormWrapperNode);
    },
  };
};
